import { BarChart3, LoaderCircle } from 'lucide-react'
import { useState } from 'react'

import type { DayRange } from './DayStrip'
import { addDays, firstOfMonth, formatDayFull, formatMonth, lastOfMonth, spanDays } from './days'
import { countedDaysOf, type TrendPoint } from './receiptsDigest'
import { centerScrollTarget } from './scrollCenter'
import { TrendSum } from './TrendSum'
import './progress.css'

/** Минимальная высота столбца графика, px: день без прихода остаётся видимым. */
const MIN_BAR_HEIGHT = 2
/** Высота самого высокого столбца, px: график компактный, сумма и число чеков читаются подписями. */
const MAX_BAR_HEIGHT = 64

/** Месячный график не сужается вместе с отбором: столбец выбирает день, границы — несколько дней. */
export function MonthTrend({ month, today, value, points, busy = false, todayBusy = false, message, onChange }: {
  month: string
  /** Сегодняшний день: в текущем месяце под столбцами рисуется линия учёта дней (какие дни вошли в среднее). */
  today?: string
  value: DayRange | null
  points: readonly TrendPoint[]
  busy?: boolean
  /** Перечитывается только сегодняшний день («Обновить»): загрузка — на его столбце, остальные дни остаются на месте. */
  todayBusy?: boolean
  message?: string
  onChange: (range: DayRange) => void
}) {
  const start = firstOfMonth(month)
  const end = lastOfMonth(month)
  const count = spanDays(start, end)
  const key = `${start}:${value?.from ?? ''}:${value?.to ?? ''}`
  const [draft, setDraft] = useState<(DayRange & { key: string }) | null>(null)
  const selected = draft?.key === key ? draft : value
  const from = selected === null || selected.from < start ? 1 : Math.min(count, spanDays(start, selected.from))
  const to = selected === null || selected.to > end ? count : Math.max(from, spanDays(start, selected.to))
  const byDay = new Map(points.map((point) => [point.key, point]))
  const top = Math.max(0, ...points.map((point) => point.saleSum))
  const tracked = today !== undefined && today >= start && today <= end
  const counted = tracked ? countedDaysOf(points, today) : new Set<string>()
  // На узком экране график шире окна: при показе месяца к центру видимой части прокручивается выбранный день, без выбора — сегодня.
  const focusDay = value !== null && value.from >= start && value.from <= end ? value.from : tracked ? today : null
  const rangeOf = (edge: 'from' | 'to', raw: string): DayRange => {
    const day = Math.max(1, Math.min(count, Number(raw)))
    return { from: addDays(start, (edge === 'from' ? Math.min(day, to) : from) - 1),
      to: addDays(start, (edge === 'to' ? Math.max(day, from) : to) - 1) }
  }
  const commit = (range: DayRange) => {
    setDraft(null)
    if (value === null || value.from !== range.from || value.to !== range.to) onChange(range)
  }
  return (
    <section className="krp-month-trend" aria-label="Динамика за месяц" aria-busy={busy}>
      <div className="krp-month-trend__heading">
        <h3 className="krp-trend__title"><BarChart3 size={16} aria-hidden="true" /> Динамика: приход по дням · {formatMonth(start)}</h3>
        <span className="krp-month-trend__status" role="status">
          {busy && <><LoaderCircle size={14} className="krp-steps__spin" aria-hidden="true" /> Загружаем динамику за месяц…</>}
          {!busy && todayBusy && <><LoaderCircle size={14} className="krp-steps__spin" aria-hidden="true" /> Обновляем сегодняшний день…</>}
        </span>
      </div>
      <div className="krp-month-trend__scroll" ref={centerScrollTarget(start)}>
        {message !== undefined && <p className="krp-receipt-note krp-month-trend__message" role="status">{message}</p>}
        <div className="krp-month-trend__plot">
          <ol className="krp-month-trend__bars" style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}>
            {Array.from({ length: count }, (_, index) => {
              const day = addDays(start, index)
              const point = byDay.get(day)
              const pressed = selected !== null && day >= selected.from && day <= selected.to
              const text = point?.text ?? `${formatDayFull(day)}: ${busy || message !== undefined ? 'данные недоступны' : 'чеков нет'}`
              const loading = todayBusy && day === today
              return <li key={day} {...(day === today ? { 'data-today': true } : {})} {...(day === focusDay ? { 'data-scroll-target': true } : {})} {...(loading ? { 'aria-busy': true } : {})}>
                <button type="button" className="krp-month-trend__bar" aria-label={text} title={text}
                  aria-pressed={pressed} onClick={() => commit({ from: day, to: day })}>
                  <span className="krp-trend__thousands" aria-hidden="true">
                    {loading ? <LoaderCircle size={14} className="krp-steps__spin" /> : <TrendSum text={point?.thousands ?? ''} />}
                  </span>
                  <span className="krp-trend__value">{point?.count ?? (busy || message !== undefined ? '—' : 0)}</span>
                  <span className="krp-trend__fill" data-empty={(point?.count ?? 0) === 0} aria-hidden="true"
                    style={{ height: `${top === 0 ? MIN_BAR_HEIGHT : Math.max(MIN_BAR_HEIGHT, Math.round(((point?.saleSum ?? 0) / top) * MAX_BAR_HEIGHT))}px` }} />
                  <span className="krp-trend__label">{index + 1}</span>
                </button>
              </li>
            })}
          </ol>
          {tracked && <ol className="krp-month-trend__rail" aria-hidden="true" style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}>
            {Array.from({ length: count }, (_, index) => {
              const day = addDays(start, index)
              // Сегодняшний день пунктиром, пока он не учтён; учтён (приход выше среднего) — зелёная точка, как у прошедших.
              const state = counted.has(day) ? 'counted' : day === today ? 'today' : 'none'
              return <li key={day} className="krp-month-trend__tick" data-state={state} data-today={day === today || undefined} />
            })}
          </ol>}
          <div className="krp-month-trend__range" role="group" aria-label="Диапазон дней месяца"
            style={{ marginInline: `${50 / count}%` }}>
            <span className="krp-month-trend__track" aria-hidden="true" />
            <span className="krp-month-trend__selection" aria-hidden="true"
              style={{ left: `${((from - 1) / (count - 1)) * 100}%`, right: `${((count - to) / (count - 1)) * 100}%` }} />
            {(['from', 'to'] as const).map((edge) => <input key={edge} type="range" min={1} max={count} step={1}
              className={`krp-month-trend__handle krp-month-trend__handle--${edge}`}
              aria-label={edge === 'from' ? 'Начало диапазона' : 'Конец диапазона'}
              aria-valuetext={formatDayFull(addDays(start, (edge === 'from' ? from : to) - 1))}
              value={edge === 'from' ? from : to}
              onChange={(event) => setDraft({ key, ...rangeOf(edge, event.target.value) })}
              onPointerUp={(event) => commit(rangeOf(edge, event.currentTarget.value))}
              onPointerCancel={() => setDraft(null)}
              onKeyUp={(event) => { if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) commit(rangeOf(edge, event.currentTarget.value)) }}
              onBlur={(event) => { if (draft?.key === key) commit(rangeOf(edge, event.currentTarget.value)) }} />)}
          </div>
        </div>
      </div>
    </section>
  )
}
